import React, { useState, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Clock, Video, DollarSign, User } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const ConsultationBooking = () => {
  const { token, user } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState([]);
  const [exchangeRates, setExchangeRates] = useState({});
  const [baseCurrency, setBaseCurrency] = useState("INR");
  const [userCurrency, setUserCurrency] = useState("INR");
  const [currencySymbol, setCurrencySymbol] = useState("₹");
  const [mentors, setMentors] = useState([]);
  
  const [formData, setFormData] = useState({
    topic: "",
    scheduled_date: "",
    scheduled_time: "",
    duration_minutes: 30,
    counselor_preference: "",
    notes: ""
  });
  
  const [ratePerMinute, setRatePerMinute] = useState(1.67);

  React.useEffect(() => {
    if (user) {
      fetchRatesAndCurrency();
      fetchMentors();
    }
  }, [user]);

  const fetchMentors = async () => {
    try {
      const response = await axios.get(`${API}/mentors`);
      setMentors(response.data);
    } catch (error) {
      console.error("Failed to load mentors", error);
    }
  };

  const fetchRatesAndCurrency = async () => {
    try {
      const [consultRatesRes, exchangeRatesRes, currenciesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/consultation-rates`),
        axios.get(`${API}/exchange-rates`),
        axios.get(`${API}/currencies`),
        axios.get(`${API}/admin/currency-settings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({data: {base_currency: "INR"}}))
      ]);

      const ratesData = consultRatesRes.data.rates || [];
      setRates(ratesData);
      
      // Get default video consultation rate per minute
      const videoRate = ratesData.find(r => r.consultation_type === 'video');
      if (videoRate) {
        setRatePerMinute(videoRate.rate_per_minute || 1.67);
      }

      // Build exchange rates map
      const ratesMap = {};
      exchangeRatesRes.data.rates.forEach(rate => {
        const key = `${rate.from_currency}_${rate.to_currency}`;
        ratesMap[key] = rate.rate;
      });
      setExchangeRates(ratesMap);
      
      const base = settingsRes.data?.base_currency || "INR";
      setBaseCurrency(base);
      
      const userCurr = user?.preferred_currency || "INR";
      setUserCurrency(userCurr);
      
      const currency = currenciesRes.data.currencies.find(c => c.code === userCurr);
      setCurrencySymbol(currency?.symbol || "₹");
    } catch (error) {
      console.error("Failed to load rates and currency data", error);
    }
  };

  const convertPrice = (price) => {
    if (baseCurrency === userCurrency) return price;
    
    const rateKey = `${baseCurrency}_${userCurrency}`;
    const rate = exchangeRates[rateKey];
    
    if (rate) {
      return (price * rate).toFixed(2);
    }
    return price;
  };

  const getVideoRate = () => {
    const videoRate = rates.find(r => r.consultation_type === 'video' && r.is_active);
    return videoRate || { rate_per_minute: 1.67 };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.topic || !formData.scheduled_date || !formData.scheduled_time) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      
      // Use direct booking endpoint (without Stripe for testing)
      const response = await axios.post(
        `${API}/consultations/book-direct`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.consultation_id) {
        toast.success("Consultation booked successfully!");
        navigate("/consultations");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book consultation");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-bold mb-2">Book a Career Consultation</h1>
            <p className="text-gray-600">Get personalized guidance from our career counselors</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6 text-center">
                <Video className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                <p className="font-medium">One-on-One Video Call</p>
                <p className="text-sm text-gray-600">Live consultation with expert</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <DollarSign className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                <p className="font-medium">{currencySymbol}{ratePerMinute}/minute</p>
                <p className="text-sm text-gray-600">Flexible duration - Choose your time</p>
                {baseCurrency !== userCurrency && (
                  <p className="text-xs text-gray-500 mt-1">
                    Rate may vary based on currency
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Booking Details</CardTitle>
              <CardDescription>Fill in the information below to schedule your consultation</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="topic">Topic / Area of Discussion *</Label>
                  <Input
                    id="topic"
                    name="topic"
                    value={formData.topic}
                    onChange={handleChange}
                    placeholder="e.g., Career transition, College selection, Skill development"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="scheduled_date" className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Preferred Date *
                    </Label>
                    <Input
                      id="scheduled_date"
                      name="scheduled_date"
                      type="date"
                      value={formData.scheduled_date}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="scheduled_time" className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Preferred Time *
                    </Label>
                    <Input
                      id="scheduled_time"
                      name="scheduled_time"
                      type="time"
                      value={formData.scheduled_time}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                {/* Duration and Pricing */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <Label htmlFor="duration_minutes">Duration (minutes) *</Label>
                        <span className="text-sm text-blue-600 font-medium">
                          Rate: {currencySymbol}{ratePerMinute}/min
                        </span>
                      </div>
                      <Input
                        id="duration_minutes"
                        name="duration_minutes"
                        type="number"
                        value={formData.duration_minutes}
                        onChange={handleChange}
                        min="15"
                        step="15"
                        required
                        className="text-lg font-semibold"
                      />
                      <div className="flex gap-2">
                        <Button type="button" onClick={() => setFormData({...formData, duration_minutes: 30})} variant="outline" size="sm">30 min</Button>
                        <Button type="button" onClick={() => setFormData({...formData, duration_minutes: 45})} variant="outline" size="sm">45 min</Button>
                        <Button type="button" onClick={() => setFormData({...formData, duration_minutes: 60})} variant="outline" size="sm">60 min</Button>
                        <Button type="button" onClick={() => setFormData({...formData, duration_minutes: 90})} variant="outline" size="sm">90 min</Button>
                      </div>
                      <div className="pt-2 border-t border-blue-300">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-700">Total Cost:</span>
                          <span className="text-2xl font-bold text-blue-600">
                            {currencySymbol}{(formData.duration_minutes * ratePerMinute).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div>
                  <Label htmlFor="counselor_preference" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Counselor Preference (Optional)
                  </Label>
                  <Select 
                    value={formData.counselor_preference || "no_preference"} 
                    onValueChange={(value) => handleSelectChange("counselor_preference", value === "no_preference" ? "" : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a counselor (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no_preference">No preference</SelectItem>
                      {mentors.map((mentor) => (
                        <SelectItem key={mentor.id} value={mentor.name}>
                          {mentor.name} - {mentor.profession}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {mentors.length === 0 
                      ? "Loading counselors..." 
                      : `${mentors.length} counselor(s) available`}
                  </p>
                </div>

                <div>
                  <Label htmlFor="notes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Any specific topics or questions you want to discuss..."
                    rows={4}
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">What to Expect</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• You'll receive a meeting link after payment confirmation</li>
                    <li>• Our counselor will reach out 24 hours before the session</li>
                    <li>• Session recording will be available for 7 days</li>
                    <li>• You can reschedule up to 48 hours in advance</li>
                  </ul>
                </div>

                <Button type="submit" className="w-full" size="lg" disabled={loading}>
                  {loading ? "Processing..." : `Book Consultation - ${currencySymbol}${(formData.duration_minutes * ratePerMinute).toFixed(2)}`}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ConsultationBooking;
